import {
  isEligibleForTourUpsell,
  useTouringTreatmentForZuidAndListing,
  useTourUpsellCookie,
} from '@zg-rentals/touring';
import {
  useSatelliteWebLandlordLeadManagementHandler_getScreeningCriteria,
  useSatelliteWebLandlordPropertyHandler_getAddressDetails,
} from '@zg-rentals/ts-satellite-client-react-query';
import { AttachmentGuidelines } from '@zg-rentals/satellite-components';
import Head from 'next/head';
import rentalTourIllustration from '../../images/rental-tour.svg';
import { useRouter } from 'next/router';
import { useContext, useEffect, useRef, useState } from 'react';
import { LeadManagementContextProvider } from '../../contexts/LeadManagementContextProvider';
import Layout from '../../components/Layout';
import { Alert, Banner, Box, Button, Heading, Page, TextButton } from '@zillow/constellation-10';
import { IconCalendarFilled, IconChecklistFilled, IconChevronLeftFilled } from '@zillow/constellation-icons';
import useReferringListingAlias from '../../hooks/useReferringListingAlias';
import StringConstants from '../../constants/StringConstants';
import PageHeading from '../../components/PageHeading';
import LeadManagementCards from '../../components/LeadManagementCards';
import LeadManagementTable from '../../components/LeadManagementTable';
import LeadManagementTableSkeleton from '../../components/LeadManagementTable/Skeleton';
import LeadManagementCardsSkeleton from '../../components/LeadManagementCards/Skeleton';
import useDevices from '../../hooks/useDevices';
import {
  SatelliteConversationFolderTypeV2,
  SatelliteConversationReadRepliedStatusTypeV2,
  SatelliteConversationRenterProgressStatusTypeV2,
  SatelliteLeadManagementSourceType,
} from '@zg-rentals/ts-satellite-client';
import useLeadManagementTableQuery from '../../hooks/useLeadManagementTableQuery';
import useLeadManagementListingAliases from '../../hooks/useLeadManagementListingAliases';
import useLeadManagementAvailableSources from '../../hooks/useLeadManagementAvailableSources';
import { isLeadSourceVisible } from '../../hooks/useIsLeadSourceVisible';
import useStreetEasyLeadManagementEnabled from '../../hooks/useStreetEasyLeadManagementEnabled';
import useLeadManagementTourEntryPointEnabled from '../../hooks/useLeadManagementTourEntryPointEnabled';
import getFolderFromQueryParam from '../../utils/getFolderFromQueryParam';
import getTimezoneData from '../../utils/dateTime/getTimezoneData';
import getLeadCountHeading from '../../utils/getLeadCountHeading';
import getPropertyDashboardOverviewUrl from '../../utils/getPropertyDashboardOverviewUrl';
import getTourCalendarUrl from '../../utils/getTourCalendarUrl';
import canViewTourCalendar from '../../utils/canViewTourCalendar';
import LeadManagementFilters from '../../components/LeadManagementFilters';
import getValueFromEnum from '../../utils/getValueFromEnum';
import useModal from '../../hooks/useModal';
import { emitEditScreeningCriteriaEvent } from '../../constants/clickstreamEvents/screeningCriteria';
import {
  emitLeadMgmtTourCalendarLinkClickEvent,
  LEAD_MGMT_TOUR_CALENDAR_LINK_CLICK_EVENT_TYPE_ID,
} from '../../constants/clickstreamEvents/tourScheduling';
import LeadManagementSearchBar from '../../components/LeadManagementSearchBar';
import EmptyState from '../../components/EmptyState';
import LeadManagementEmptyState from '../../components/LeadManagementEmptyState';
import LeadManagementListingsCombobox from '../../components/LeadManagementListingsCombobox';
import LeadManagementSourceFilter from '../../components/LeadManagementSourceFilter';
import { AppContext } from '../../contexts/AppContextProvider';
import useQueryParamsNext from '../../hooks/useQueryParamsNext';
import { LEAD_MANAGEMENT_PAGE_VIEW_EVENT_TYPE_ID } from '../../constants/clickstreamEvents/pageView';
import useEmitLeadManagementPageView from '../../hooks/useEmitLeadManagementPageView';
import LeadManagementPageSkeleton from '../../components/LeadManagementPageSkeleton';
import { withRedirectBoundary } from '../../components/RedirectBoundary';
import RenterMessagingModal from '../../components/Modals/RenterMessagingModal';
import TourSchedulingUpsellModal from '../../components/Modals/TourSchedulingUpsellModal';
import RenterScreeningCriteriaModal from '../../components/Modals/RenterScreeningCriteriaModal';
import { ModalType } from '../../types/ModalType';
import { useAuth } from '../../AuthProvider';
import { isHttpAuthError } from '../../utils/errorUtils';
import WrongAccountAlert from '../../components/WrongAccountAlert';
import formatListingAddress from '../../utils/formatListingAddress';
import useHasProperties from '../../hooks/useHasProperties';
import {
  leadManagementHeaderStyles,
  leadManagementScopeRowStyles,
  leadManagementTitleActionsStyles,
  leadManagementTitleRowStyles,
} from '../../styles/leadManagementStyles';
import { useMediaQuery } from '@zg-rentals/react-hooks';
import {
  CLICK_ON_SHARE_FEEDBACK_EVENT_TYPE_ID,
  emitClickOnShareFeedbackEvent,
  emitSelectPropertyEvent,
} from '../../constants/clickstreamEvents/leadManagementTable';

// Narrowest desktop width where the heading, search, listings, source and filters share one row.
const WIDE_HEADER_MEDIA_QUERY = '(min-width: 1440px)';

const FEEDBACK_FORM_URL =
  'https://docs.google.com/forms/d/e/1FAIpQLSfIBc53DQBNv3MW80eeppNcvZoBjXtXHEFUJXFOVlgOWspjkQ/viewform';

export default withRedirectBoundary(
  () => {
    const router = useRouter();

    const { user } = useAuth();
    const listingAliases = useLeadManagementListingAliases();
    // Ref updates synchronously so rapid clicks don't re-emit against a stale URL snapshot.
    const lastEmittedAliasesRef = useRef<Array<string>>(listingAliases);
    useEffect(() => {
      lastEmittedAliasesRef.current = listingAliases;
    }, [listingAliases]);
    const [conversationIdToFilterBy, setConversationIdToFilterBy] = useState<string | null>(null);
    const { paths } = useContext(AppContext);
    const { isMobileView, isWindowWidthSet } = useDevices();
    const { removeAllQueryParams, updateQueryParams } = useQueryParamsNext();
    const { activeModal, clearModal, setActiveModal } = useModal();
    const { query } = router;
    const {
      conversationId,
      folder,
      listingAlias,
      messagingConversationId,
      minCriteriaMatches,
      readRepliedStatus,
      renterProgressStatus,
      source,
    } = query;
    const { clientTimeZone } = getTimezoneData();
    const isStreetEasyLeadManagementEnabled = useStreetEasyLeadManagementEnabled();
    const isTourEntryPointEnabled = useLeadManagementTourEntryPointEnabled();

    // Empty when 0 or >1 selected so per-listing APIs (tours, criteria, modals) self-disable.
    const singleListingAlias = listingAliases.length === 1 ? listingAliases[0] : '';

    const queryParamValues = {
      conversationId: conversationId?.toString() || '',
      folder: getFolderFromQueryParam(folder?.toString() || SatelliteConversationFolderTypeV2.INBOX),
      listingAlias: singleListingAlias,
      messagingConversationId: messagingConversationId?.toString() || '',
      minCriteriaMatches: Number(minCriteriaMatches) || null,
      readRepliedStatus:
        getValueFromEnum(readRepliedStatus?.toString(), SatelliteConversationReadRepliedStatusTypeV2) ||
        SatelliteConversationReadRepliedStatusTypeV2.SHOW_ALL,
      renterProgressStatus:
        getValueFromEnum(renterProgressStatus?.toString(), SatelliteConversationRenterProgressStatusTypeV2) ||
        SatelliteConversationRenterProgressStatusTypeV2.SHOW_ALL,
      // Flag off ignores the param entirely so the request shape is unchanged pre-rollout.
      source: isStreetEasyLeadManagementEnabled
        ? (getValueFromEnum<typeof SatelliteLeadManagementSourceType>(
            source?.toString(),
            SatelliteLeadManagementSourceType,
          ) ?? null)
        : null,
    };

    const { hasUpsellCookieSetForListing } = useTourUpsellCookie(queryParamValues.listingAlias);
    const hasListingAlias = Boolean(listingAlias);

    useEmitLeadManagementPageView(user, queryParamValues.listingAlias);

    const {
      availableSources: responseAvailableSources,
      leads,
      totalLeadCount,
      isLoading: isLoadingLeadsData,
      isError: hasErrorLoadingLeadsData,
      error: leadsQueryError,
    } = useLeadManagementTableQuery(
      {
        clientTimeZone,
        conversationId: conversationIdToFilterBy,
        listingAliases,
        folder: queryParamValues.folder,
        minCriteriaMatches: queryParamValues.minCriteriaMatches,
        readRepliedStatus: queryParamValues.readRepliedStatus,
        renterProgressStatus: queryParamValues.renterProgressStatus,
        source: queryParamValues.source,
      },
      { enabled: router.isReady },
    );
    const availableSources = useLeadManagementAvailableSources({
      availableSources: responseAvailableSources,
      conversationIdToFilterBy,
      listingAliases,
    });
    const showSourceColumn = isLeadSourceVisible(availableSources, isStreetEasyLeadManagementEnabled);
    // The Source filter also stays up while a source is selected so an empty filtered page can be cleared.
    const hasSourceFilter = showSourceColumn || queryParamValues.source !== null;
    const hasWideHeader = useMediaQuery(WIDE_HEADER_MEDIA_QUERY, true);
    const wantsScopeRowAboveHeader = isMobileView || (hasSourceFilter && !hasWideHeader);
    // Moving the listings selector between the header and the selector row remounts it, which
    // closes its listbox; hold the current placement until the user is done picking listings.
    const [isListingsComboboxOpen, setIsListingsComboboxOpen] = useState(false);
    const [showScopeRowAboveHeader, setShowScopeRowAboveHeader] = useState(wantsScopeRowAboveHeader);
    if (!isListingsComboboxOpen && showScopeRowAboveHeader !== wantsScopeRowAboveHeader) {
      setShowScopeRowAboveHeader(wantsScopeRowAboveHeader);
    }

    const canShowTourUpsell =
      !hasUpsellCookieSetForListing && !queryParamValues.conversationId && !!queryParamValues.listingAlias;
    const { data: touringTreatmentData, isSuccess: isTouringTreatmentSuccess } = useTouringTreatmentForZuidAndListing(
      {
        listingAlias: queryParamValues.listingAlias,
      },
      {
        enabled: isTourEntryPointEnabled ? !!queryParamValues.listingAlias : canShowTourUpsell,
        errorDetails: 'LeadManagementPage - Error fetching touring treatment',
      },
    );
    // A failed refetch keeps the last good data, so require success like ZRM's redirect does.
    const showTourCalendarLink =
      isTourEntryPointEnabled &&
      !!queryParamValues.listingAlias &&
      isTouringTreatmentSuccess &&
      canViewTourCalendar(touringTreatmentData);

    // Listing-level details are per-row to support multi-listing; derive from the first lead.
    const firstLeadListingDetails = leads[0]?.listingDetails;
    const heading = getLeadCountHeading({
      totalLeadCount,
      isLoading: isLoadingLeadsData,
      hasError: hasErrorLoadingLeadsData,
    });
    const hasNoLeadsToDisplay = !isLoadingLeadsData && !hasErrorLoadingLeadsData && leads?.length === 0;
    const listingAddress = formatListingAddress(firstLeadListingDetails?.address);
    // Empty alias (0 or >1 listings selected) falls back to the properties index inside the helper.
    const propertyDashboardOverviewUrl = getPropertyDashboardOverviewUrl(
      paths.myPropertiesPageWithBasePath,
      singleListingAlias,
    );
    const { hasProperties, isLoading: isLoadingProperties } = useHasProperties();
    const { data: criteriaData } = useSatelliteWebLandlordLeadManagementHandler_getScreeningCriteria(
      { listingAlias: queryParamValues.listingAlias },
      {
        enabled: !!queryParamValues.listingAlias,
        meta: {
          tags: ['satellite:propertyScreeningCriteria'],
          context: {
            api: 'getScreeningCriteria',
            details: 'LeadManagementPage - Error fetching criteria',
          },
        },
      },
    );
    const isScreeningCriteriaEnabled = criteriaData?.isEnabled ?? true;

    const hasFiltersApplied =
      !!folder || !!minCriteriaMatches || !!readRepliedStatus || !!renterProgressStatus || !!queryParamValues.source;
    const hasErrorWithNoData = hasErrorLoadingLeadsData && leads.length === 0;
    const shouldDisplayAlertMessage =
      hasErrorWithNoData || hasNoLeadsToDisplay || (hasNoLeadsToDisplay && hasFiltersApplied);
    // The "Back to property" link reflects the property the user navigated from (the referrer), not
    // the current selection or first lead — so it stays correct across multi-listing filtering. The
    // address is resolved from the referring alias directly, independent of which leads are loaded.
    const referringListingAlias = useReferringListingAlias();
    const { data: referringPropertyAddressData } = useSatelliteWebLandlordPropertyHandler_getAddressDetails(
      { listingAlias: referringListingAlias ?? '' },
      {
        enabled: !!referringListingAlias,
        meta: {
          context: {
            api: 'getAddressDetails',
            details: 'LeadManagementPage - Error fetching referring property address for back link',
          },
        },
      },
    );
    const backToPropertyUrl = getPropertyDashboardOverviewUrl(
      paths.myPropertiesPageWithBasePath,
      referringListingAlias ?? '',
    );
    const backToPropertyAddress = referringPropertyAddressData?.displayAddress ?? '';

    const shouldShowTourUpsellModal =
      canShowTourUpsell &&
      isEligibleForTourUpsell(touringTreatmentData) &&
      !isLoadingLeadsData &&
      leads &&
      leads.length > 0;

    useEffect(() => {
      if (shouldShowTourUpsellModal) {
        setActiveModal(ModalType.TourSchedulingUpsellModal);
      }
      // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
    }, [shouldShowTourUpsellModal]);

    if (!hasListingAlias && (!router.isReady || isLoadingProperties)) {
      return null;
    }

    if (!hasListingAlias && !hasProperties) {
      return (
        <Layout>
          {/* oxlint-disable-next-line next/no-duplicate-head -- mutually exclusive early-return branch; never renders alongside the other <Head> below */}
          <Head>
            <title>{StringConstants.LEAD_MANAGEMENT_PAGE_TITLE}</title>
          </Head>
          <EmptyState
            illustration={rentalTourIllustration}
            heading={StringConstants.LEADS_EMPTY_STATE_HEADING}
            body={StringConstants.LEADS_NO_PROPERTIES_BODY}
            css={{ justifyContent: 'center', flex: 1, minHeight: 'calc(100vh - 120px)', paddingInline: 'default' }}
            cta={
              <Button tone="brand" emphasis="filled" asChild>
                <a href={paths.myPropertiesPageWithBasePath}>Add property</a>
              </Button>
            }
          />
        </Layout>
      );
    }

    const handleEditScreeningCriteriaClick = () => {
      emitEditScreeningCriteriaEvent(user, { listingAlias: queryParamValues.listingAlias });
      setActiveModal(ModalType.RenterScreeningCriteriaModal);
    };

    const handleClearFilters = () => {
      removeAllQueryParams({
        listingAlias: listingAlias?.toString() ?? '',
        shouldReplaceHistory: true,
      });
      setConversationIdToFilterBy(null);
    };

    const handleListingAliasesChange = (aliases: Array<string>) => {
      const addedAliases = aliases.filter((alias) => !lastEmittedAliasesRef.current.includes(alias));
      lastEmittedAliasesRef.current = aliases;
      for (const addedAlias of addedAliases) {
        emitSelectPropertyEvent(user, { listingAlias: addedAlias });
      }
      updateQueryParams({
        queryParamValues: { listingAlias: aliases.length > 0 ? aliases.join(',') : null },
        pathname: '/lead-management',
      });
    };

    return (
      <Layout>
        <Head>
          <title>{StringConstants.LEAD_MANAGEMENT_PAGE_TITLE}</title>
        </Head>
        {activeModal === ModalType.RenterScreeningCriteriaModal && (
          <RenterScreeningCriteriaModal
            isOpen={true}
            onOpenChange={(open) => !open && clearModal()}
            listingAlias={queryParamValues.listingAlias}
          />
        )}
        <LeadManagementContextProvider
          value={{
            availableSources,
            clientTimeZone,
            conversationId: queryParamValues.conversationId,
            conversationIdToFilterBy,
            folder: queryParamValues.folder,
            isArchivedFolder: queryParamValues.folder === SatelliteConversationFolderTypeV2.ARCHIVED,
            isReportedFolder: queryParamValues.folder === SatelliteConversationFolderTypeV2.SPAM,
            listingAlias: queryParamValues.listingAlias,
            listingAliases,
            messagingConversationId: queryParamValues.messagingConversationId,
            minCriteriaMatches: queryParamValues.minCriteriaMatches,
            readRepliedStatus: queryParamValues.readRepliedStatus,
            renterProgressStatus: queryParamValues.renterProgressStatus,
            source: queryParamValues.source,
          }}
        >
          {shouldShowTourUpsellModal && (
            <TourSchedulingUpsellModal
              modalHeaderText={listingAddress}
              listingAlias={queryParamValues?.listingAlias || ''}
            />
          )}

          {activeModal === ModalType.AttachmentModal && <AttachmentGuidelines onClose={clearModal} />}

          {queryParamValues.messagingConversationId && (
            <RenterMessagingModal conversationId={queryParamValues.messagingConversationId} />
          )}
          <Banner
            tone="info"
            actionButton={
              <TextButton onImpact asChild>
                <a
                  data-eventid={CLICK_ON_SHARE_FEEDBACK_EVENT_TYPE_ID}
                  href={FEEDBACK_FORM_URL}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => emitClickOnShareFeedbackEvent(user, { listingAlias: queryParamValues.listingAlias })}
                >
                  Share feedback
                </a>
              </TextButton>
            }
            role="status"
          >
            Help inform future improvements to this page
          </Banner>

          <Page.Content
            fluid
            data-eventid={LEAD_MANAGEMENT_PAGE_VIEW_EVENT_TYPE_ID}
            data-eventdescription="Page view"
            css={{ paddingInline: 'loose' }}
          >
            {referringListingAlias && (
              <TextButton asChild icon={<IconChevronLeftFilled />}>
                <a href={backToPropertyUrl}>
                  Back to {backToPropertyAddress ? backToPropertyAddress : 'property dashboard'}
                </a>
              </TextButton>
            )}
            <Box css={leadManagementTitleRowStyles}>
              <PageHeading heading="Leads" />
              {(showTourCalendarLink || isScreeningCriteriaEnabled) && (
                <Box css={leadManagementTitleActionsStyles}>
                  {showTourCalendarLink && (
                    <Button
                      asChild
                      icon={<IconCalendarFilled />}
                      data-eventid={LEAD_MGMT_TOUR_CALENDAR_LINK_CLICK_EVENT_TYPE_ID}
                      css={{ flexShrink: 0 }}
                    >
                      <a
                        href={getTourCalendarUrl({ listingAlias: queryParamValues.listingAlias })}
                        onClick={() =>
                          emitLeadMgmtTourCalendarLinkClickEvent(user, { listingAlias: queryParamValues.listingAlias })
                        }
                      >
                        Tour calendar
                      </a>
                    </Button>
                  )}
                  {isScreeningCriteriaEnabled && (
                    <Button
                      icon={<IconChecklistFilled />}
                      onClick={handleEditScreeningCriteriaClick}
                      disabled={isLoadingProperties || !hasProperties}
                      css={{ flexShrink: 0 }}
                    >
                      Manage screening criteria
                    </Button>
                  )}
                </Box>
              )}
            </Box>

            {showScopeRowAboveHeader && (
              <Box css={leadManagementScopeRowStyles}>
                <LeadManagementListingsCombobox
                  selectedListingAliases={listingAliases}
                  onChange={handleListingAliasesChange}
                  onOpenChange={setIsListingsComboboxOpen}
                  fullWidth
                />
                <LeadManagementSourceFilter fullWidth />
              </Box>
            )}

            <Box css={leadManagementHeaderStyles}>
              <Heading level={2} css={{ flexShrink: 0, textStyle: { xlDown: 'h6', xl: 'h4' }, marginRight: 'auto' }}>
                {heading}
              </Heading>
              <LeadManagementSearchBar
                isDisabled={isLoadingLeadsData}
                setConversationIdToFilterBy={setConversationIdToFilterBy}
              />
              {!showScopeRowAboveHeader && (
                <>
                  <LeadManagementListingsCombobox
                    selectedListingAliases={listingAliases}
                    onChange={handleListingAliasesChange}
                    onOpenChange={setIsListingsComboboxOpen}
                  />
                  <LeadManagementSourceFilter />
                </>
              )}
              <LeadManagementFilters />
            </Box>
            {isLoadingLeadsData && !isWindowWidthSet ? (
              <>
                <Box css={{ xlDown: { display: 'none' } }}>
                  <LeadManagementTableSkeleton showSourceColumn={showSourceColumn} />
                </Box>

                <Box css={{ xl: { display: 'none' } }}>
                  <LeadManagementCardsSkeleton />
                </Box>
              </>
            ) : (
              <>
                {shouldDisplayAlertMessage ? (
                  <>
                    {hasErrorWithNoData && (
                      <Box css={{ marginTop: 'tighter', marginBottom: 'default' }}>
                        {isHttpAuthError(leadsQueryError) ? (
                          <WrongAccountAlert textLabel="listing" />
                        ) : (
                          <Alert tone="critical">{StringConstants.GENERIC_ERROR}</Alert>
                        )}
                      </Box>
                    )}

                    {hasNoLeadsToDisplay && !hasFiltersApplied && (
                      <LeadManagementEmptyState
                        clientTimeZone={clientTimeZone}
                        listingAliases={listingAliases}
                        propertyDashboardOverviewUrl={propertyDashboardOverviewUrl}
                      />
                    )}

                    {hasNoLeadsToDisplay && hasFiltersApplied && (
                      <EmptyState
                        illustration={rentalTourIllustration}
                        heading="No leads match the filters applied"
                        withCard
                        css={{ marginTop: 'tighter' }}
                        cta={
                          <Button tone="brand" emphasis="filled" onClick={handleClearFilters}>
                            Reset filters
                          </Button>
                        }
                      />
                    )}
                  </>
                ) : (
                  <>{isMobileView ? <LeadManagementCards /> : <LeadManagementTable />}</>
                )}
              </>
            )}
          </Page.Content>
        </LeadManagementContextProvider>
      </Layout>
    );
  },
  {
    loading: <LeadManagementPageSkeleton />,
  },
);
